import { z } from 'zod';

import { ApiError } from '@mds/shared/lib/api';
import { dateLabel } from '@mds/shared/lib/format';
import {
  BOOKING_PAYMENT_METHODS,
  type AdminBooking,
  type BookingDisplayStatus,
  type BookingPaymentMethod,
} from '@mds/shared/types/booking';

import { clockRange } from '@mds/shared/lib/dates';
import { optionalText, picked } from '@/lib/validation';

/**
 * The vocabulary the booking and payment screens share: how the API's keys are
 * spelled for the front desk, and how its two conflict codes are recognised.
 */

const METHODS: Record<BookingPaymentMethod, string> = {
  cash: 'Cash',
  whish: 'Whish',
  omt: 'OMT',
  other: 'Other',
};

/** Every method money can be taken as, in the order the API declares them. */
export const PAYMENT_METHOD_OPTIONS = BOOKING_PAYMENT_METHODS.map((value) => ({
  value,
  label: METHODS[value],
}));

const STATUS_LABELS: Record<BookingDisplayStatus, string> = {
  payment_pending: 'Unpaid',
  confirmed: 'Confirmed',
  deposit_due: 'Balance due',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

/** The status as the front desk reads it, not as the API stores it. */
export const bookingStatusLabel = (status: BookingDisplayStatus) => STATUS_LABELS[status];

const STATUS_TONES: Record<BookingDisplayStatus, string> = {
  // Nothing is held until money is taken, so an unpaid booking reads as urgent
  // rather than as a booking that merely owes a balance.
  payment_pending: 'text-danger bg-danger-wash border-danger-edge',
  deposit_due: 'text-warn bg-warn-wash border-warn-edge',
  confirmed: 'text-mint-deep bg-mint-wash border-mint/35',
  completed: 'text-ink-muted bg-ink/[0.05] border-line',
  cancelled: 'text-ink-muted bg-ink/[0.05] border-line',
};

/** The border/fill/text classes a booking status is drawn in. */
export const bookingStatusTone = (status: BookingDisplayStatus) => STATUS_TONES[status];

/**
 * The time a booking is held. Rooms are held for a run of hours; a desk for the
 * whole day, so it has no times — which desk it is belongs with the space, in
 * `bookingSpace`.
 */
export const bookingTime = (booking: AdminBooking) =>
  booking.startMinute !== null && booking.endMinute !== null
    ? clockRange(booking.startMinute, booking.endMinute)
    : 'All day';

/**
 * When a booking is held: its date and its time together. The date is read as a
 * local calendar day, so it never slips to the day before west of UTC.
 */
export const bookingWhen = (booking: AdminBooking) =>
  `${dateLabel(booking.bookingDate)}${
    booking.bookingEndDate === booking.bookingDate ? '' : ` – ${dateLabel(booking.bookingEndDate)}`
  } · ${bookingTime(booking)}`;

/** Where a booking is: the space, and for a desk booking which desk on it. */
export const bookingSpace = (booking: AdminBooking) =>
  booking.desk ? `${booking.resource.name} · Desk ${booking.desk.deskId}` : booking.resource.name;

/**
 * The `code` the error middleware puts on every failure, or null when the
 * failure never reached the API.
 */
const errorCode = (error: unknown): string | null =>
  error instanceof ApiError ? (error.code ?? null) : null;

/** Someone else took the desk or slot between the board being read and written. */
export const isBookingConflict = (error: unknown) => errorCode(error) === 'BOOKING_CONFLICT';

/**
 * The paid total moved under the form — a second click on Deposit, or another
 * administrator collecting at the same time. The booking has to be re-read
 * before the amount means anything again.
 */
export const isPaymentStateChanged = (error: unknown) =>
  errorCode(error) === 'PAYMENT_STATE_CHANGED';

/* --------------------------------------------------------------- money --- */

/** The split the policy fixes. The admin never types a deposit. */
export const DEPOSIT_PERCENTAGE = 50;

/**
 * Half the total, rounded up by a cent when it will not divide evenly — the
 * same rule the API applies, so the amount sent is the amount it expects.
 */
export const depositAmount = (totalAmountMinor: number) => Math.ceil(totalAmountMinor / 2);

/** Whether the customer is settling now or leaving the fixed deposit. */
export type PaymentChoice = 'deposit' | 'full';

/**
 * What a payment against this booking has to be, in cents.
 *
 * The first payment is either the deposit or the whole total; after a deposit
 * the balance must be settled in full. So the amount is never typed — the
 * choice only exists while nothing has been paid.
 */
export function collectableAmount(booking: AdminBooking, choice: PaymentChoice): number {
  const { balanceAmountMinor, paidAmountMinor, requiredDepositAmountMinor, totalAmountMinor } =
    booking.payment;
  if (balanceAmountMinor <= 0) return 0;
  if (paidAmountMinor > 0) return balanceAmountMinor;
  return choice === 'deposit' ? requiredDepositAmountMinor : totalAmountMinor;
}

/* ---------------------------------------------------------- the form --- */

/** What the administrator actually chooses when money changes hands. */
export interface PaymentDraft {
  /** Only read while nothing has been paid — after that the balance is fixed. */
  choice: PaymentChoice;
  method: BookingPaymentMethod;
  /** Required when the method is `other`; ignored otherwise. */
  methodDetail: string;
  note: string;
  /**
   * What the administrator checked for a student package. Only sent — and then
   * required — with the first payment on a booking whose eligibility is pending.
   */
  evidenceNote: string;
}

export const blankPaymentDraft: PaymentDraft = {
  choice: 'full',
  method: 'cash',
  methodDetail: '',
  note: '',
  evidenceNote: '',
};

/**
 * Whether this payment has to carry the student evidence note: the package
 * asks for verification, nobody has recorded it yet, and this is the first
 * money taken. The API refuses the note on any other booking.
 */
export const needsEligibility = (booking: AdminBooking) =>
  booking.packageEligibility?.status === 'pending' && booking.payment.paidAmountMinor === 0;

/** The request fragment that carries the evidence note, or nothing when it does not apply. */
export const eligibilityPayload = (booking: AdminBooking, draft: PaymentDraft) =>
  needsEligibility(booking)
    ? { packageEligibilityVerification: { evidenceNote: draft.evidenceNote.trim() } }
    : {};

const METHOD_DETAIL = 'Type how the money was taken, or pick one of the other methods.';

/**
 * What `POST …/payments` would reject for this booking, checked here so it fails
 * before the round trip. The amount is read off the booking rather than typed,
 * so the rules left are the method, the notes and the student evidence.
 */
export const paymentSchema = (booking: AdminBooking | null) =>
  z
    .object({
      choice: z.enum(['deposit', 'full'], { error: 'Choose what is being paid.' }),
      method: picked('Choose how the money was taken.'),
      methodDetail: optionalText(80),
      note: optionalText(500),
      evidenceNote: optionalText(500),
    })
    .superRefine((draft, ctx) => {
      if (draft.method === 'other' && draft.methodDetail.trim() === '') {
        ctx.addIssue({ code: 'custom', path: ['methodDetail'], message: METHOD_DETAIL });
      }
      if (booking && needsEligibility(booking) && draft.evidenceNote.trim() === '') {
        ctx.addIssue({
          code: 'custom',
          path: ['evidenceNote'],
          message: 'Record what you checked before taking the first payment.',
        });
      }
    });

/**
 * The booking form on Spaces, less the new customer — that half is checked
 * against `customerSchema` on its own. Mirrors `POST /admin/bookings`, whose
 * first payment follows the same rules as any other.
 */
export const bookingSchema = ({
  existingCustomer,
  studentPackage,
  price,
}: {
  /** Picking from the directory rather than adding someone new. */
  existingCustomer: boolean;
  /** A package that asks for student verification before money is taken. */
  studentPackage: boolean;
  /** The total in cents, which a first payment cannot be taken against at zero. */
  price: number;
}) =>
  z
    .object({
      customerId: z.string(),
      settle: z.enum(['full', 'deposit', 'none'], { error: 'Choose what is being paid.' }),
      method: picked('Choose how the money was taken.'),
      methodDetail: optionalText(80),
      note: optionalText(10_000),
      evidenceNote: optionalText(500),
    })
    .superRefine((draft, ctx) => {
      if (existingCustomer && draft.customerId === '') {
        ctx.addIssue({
          code: 'custom',
          path: ['customerId'],
          message: 'Pick a customer, or switch to “New customer”.',
        });
      }
      if (draft.settle === 'none') return;
      if (price <= 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['settle'],
          message: 'There is nothing to pay on this booking. Choose “Nothing yet”.',
        });
      }
      if (draft.method === 'other' && draft.methodDetail.trim() === '') {
        ctx.addIssue({ code: 'custom', path: ['methodDetail'], message: METHOD_DETAIL });
      }
      if (studentPackage && draft.evidenceNote.trim() === '') {
        ctx.addIssue({
          code: 'custom',
          path: ['evidenceNote'],
          message: 'Record what you checked before taking payment, or choose “Nothing yet”.',
        });
      }
    });

/** Cancelling takes an optional reason, which the audit log keeps. */
export const cancelSchema = z.object({ reason: optionalText(500) });

/** A desk is sold by package, and only a package still on sale can be booked. */
export const packageChoiceSchema = (onSale: readonly { id: string }[]) =>
  z.object({
    packageId: z
      .string()
      .refine((id) => onSale.some((p) => p.id === id), 'Choose a desk package that is on sale.'),
  });
